import React, { Suspense, useEffect, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls, Preload, useGLTF } from "@react-three/drei";
import desktop from "/src/assets/tech/mobilepic.png";

import CanvasLoader from "../Loader";

const Computers = ({ screenSize }) => {
  const computer = useGLTF("/desktop_pc/scene-extreme-light.glb");

  // Responsive scale and position based on screen size
  const getModelProps = () => {
    switch(screenSize) {
      case 'xs': // Extra small mobile (0-400px)
        return {
          scale: 0.15,
          position: [0, -0.35, -0.15],
          lightPosition: [0, 1.5, 6],
          fov: 26
        };
      case 'sm': // Small mobile (401-640px)
        return {
          scale: 0.30,
          position: [0, -0.9, -0.49],
          lightPosition: [0, 1.5, 6],
          fov: 26
        };
      case 'md': // Small tablet (641-768px)
        return {
          scale: 0.45,
          position: [0, -2.0, -0.55],
          lightPosition: [0, 1.5, 6],
          fov: 26
        };
      case 'lg': // Large tablet (769-1024px)
        return {
          scale: 0.6,
          position: [0, -3.2, -0.25],
          lightPosition: [0, 2.5, 7],
          fov: 24
        };
      case 'xl': // Small desktop (1025-1280px)
       return {
         scale: 0.7,
          position: [0, -3.75, -1.0],
          lightPosition: [0, 3, 8],
          fov: 26
        };
      default: // Large desktop (1281px+)
        return {
          scale: 0.7,
          position: [0, -3.0, -1.0],
          lightPosition: [0, 3, 8],
          fov: 26
        };
    }
  };

  const { scale, position, lightPosition, fov } = getModelProps();

  return (
    <mesh>
      <hemisphereLight intensity={0.5} groundColor="#000000" />
      <spotLight
        position={[-20, 50, 10]}
        angle={0.12}
        penumbra={1}
        intensity={1}
        castShadow
        shadow-mapSize={1024}
      />
      <pointLight position={lightPosition} intensity={0.8} color="#A39CFF" />
      <primitive
        object={computer.scene}
        scale={scale}
        position={position}
        rotation={[-0.01, -0.2, -0.1]}
      />
    </mesh>
  );
};

const ComputersCanvas = () => {
  const [screenSize, setScreenSize] = useState('xl');
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Define breakpoints
    const breakpoints = {
      xs: '(max-width: 400px)',
      sm: '(min-width: 401px) and (max-width: 640px)',
      md: '(min-width: 641px) and (max-width: 768px)',
      lg: '(min-width: 769px) and (max-width: 1024px)',
      xl: '(min-width: 1025px) and (max-width: 1280px)'
    };

    // Create media query lists
    const queries = Object.entries(breakpoints).reduce((acc, [size, query]) => {
      acc[size] = window.matchMedia(query);
      return acc;
    }, {});
    
    // Check which breakpoint matches
    const updateScreenSize = () => {
      for (const [size, mq] of Object.entries(queries)) {
        if (mq.matches) {
          setScreenSize(size);
          setIsMobile(size === 'xs' || size === 'sm'); // Only xs and sm are mobile phones
          return;
        }
      }
      // Default to largest size if no matches
      setScreenSize('xxl');
      setIsMobile(false);
    };
    
    // Set initial size
    updateScreenSize();
    
    // Add event listeners for all breakpoints
    const mqls = Object.values(queries);
    mqls.forEach(mq => mq.addEventListener('change', updateScreenSize));
    
    // Clean up
    return () => {
      mqls.forEach(mq => mq.removeEventListener('change', updateScreenSize));
    };
  }, []);

  // Show mobile image with subtle message
  if (isMobile) {
    return (
      <div className="relative w-full h-full">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative w-full max-w-xs h-48 sm:h-56">
            <img 
              src={desktop}
              alt="Portfolio Preview"
              className="w-full h-full object-contain"
            />
            <div className="absolute -bottom-6 left-0 right-0 text-center">
              <p className="text-xs text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 font-medium">
                View on desktop for 3D experience
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Canvas
      frameloop='demand'
      shadows
      dpr={[1, 2]}
      camera={{
        position: [20, 3, 5],
        fov: screenSize === 'xs' ? 30 : 
             screenSize === 'sm' ? 28 :
             screenSize === 'md' ? 26 :
             screenSize === 'lg' ? 24 :
             screenSize === 'xl' ? 22 : 20
      }}
      gl={{ preserveDrawingBuffer: true }}
    >
      <Suspense fallback={<CanvasLoader />}>
        <OrbitControls
          enableZoom={false}
          maxPolarAngle={Math.PI / 2}
          minPolarAngle={Math.PI / 2}
          enablePan={false}
          enableRotate={!isMobile}
          autoRotate={false}
          autoRotateSpeed={0}
        />
        <Computers screenSize={screenSize} />
      </Suspense>

      <Preload all />
    </Canvas>
  );
};

export default ComputersCanvas;
